// src/features/people/components/TrainingsSection.jsx - CON RESTRICCIONES PARA PERSONAS DADAS DE BAJA
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Spinner, Row, Col } from 'react-bootstrap';
import { FaPlus, FaGraduationCap, FaChalkboardTeacher, FaBook } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import AssignTrainingModal from './AssignTrainingModal';

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

// Servicios
import PeopleService from '../PeopleService';

// Constantes
import { TIPOS_CAPACITACIONES, FORMATO_FECHA, getTipoDescripcion } from '../../../utils/constants';

// ✅ AGREGADA PROP isPersonDeactivated
const TrainingsSection = ({ personId, trainings = [], onDataChange, showSnackbar, isPersonDeactivated = false }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filteredTrainings, setFilteredTrainings] = useState(trainings);
  const navigate = useNavigate();

  const typeValueGetter = useCallback((training) => {
    return getTipoDescripcion(training.type, TIPOS_CAPACITACIONES);
  }, []);

  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType 
  } = useColumnFilter(trainings, 'type', typeValueGetter, `person-${personId}-trainings`);

  const { 
    filterOptions: topicOptions, 
    activeFilters: activeTopics,
    handleOptionsUpdate: handleTopicOptionsUpdate,
    handleFiltersChange: handleTopicChange,
    filterData: filterByTopic 
  } = useColumnFilter(trainings, 'topic', null, `person-${personId}-trainings`);

  const { 
    filterOptions: instructorOptions, 
    activeFilters: activeInstructors,
    handleOptionsUpdate: handleInstructorOptionsUpdate,
    handleFiltersChange: handleInstructorChange,
    filterData: filterByInstructor 
  } = useColumnFilter(trainings, 'instructor', null, `person-${personId}-trainings`);

  useEffect(() => {
    let result = [...trainings];
    
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    if (activeTopics && activeTopics.length > 0) {
      result = filterByTopic(result);
    }
    
    if (activeInstructors && activeInstructors.length > 0) {
      result = filterByInstructor(result);
    }
    
    setFilteredTrainings(result);
  }, [trainings, activeTypes, activeTopics, activeInstructors, filterByType, filterByTopic, filterByInstructor]);

  const handleViewTraining = useCallback((training) => {
    navigate(`/trainings/${training.id}`);
  }, [navigate]);

  const trainingColumns = useMemo(() => [
    { 
      headerName: 'Tipo', 
      field: 'type', 
      flex: 1,
      valueFormatter: params => getTipoDescripcion(params.value, TIPOS_CAPACITACIONES),
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Fecha', 
      field: 'date', 
      width: 120,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {
        return params.data.date ? dayjs(params.data.date).format(FORMATO_FECHA) : '';
      },
      floatingFilter: true
    },
    { 
      headerName: 'Temática', 
      field: 'topic', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Instructor', 
      field: 'instructor', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    }
  ], []);

  // ✅ VALIDACIÓN AGREGADA
  const handleUnassignTraining = (training) => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden desasignar capacitaciones de una persona dada de baja');
      return;
    }
    setSelectedTraining(training);
    setShowUnassignModal(true);
  };

  // ✅ VALIDACIÓN AGREGADA
  const handleOpenAssignModal = () => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden asignar capacitaciones a una persona dada de baja');
      return;
    }
    setShowAssignModal(true);
  };

  const handleTrainingAssigned = () => {
    if (showSnackbar) {
      showSnackbar('success', 'Capacitación asignada correctamente');
    }
    
    if (onDataChange) onDataChange();
  };

  const handleConfirmUnassign = async () => {
    if (!selectedTraining) return;
    
    try {
      setLoading(true);
      
      const result = await PeopleService.unassignPersonFromTraining(personId, selectedTraining.id);
      
      if (!result || !result.success) {
        throw new Error(result?.error || 'Error al dar de baja de la capacitación');
      }
      
      if (showSnackbar) {
        showSnackbar('success', 'Se ha dado de baja de la capacitación correctamente');
      }
      
      setShowUnassignModal(false);
      setSelectedTraining(null);
      
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error al dar de baja de capacitación:', error);
      if (showSnackbar) {
        showSnackbar('error', 'Error al dar de baja de la capacitación: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <FilterSelect
                options={typeOptions}
                activeFilters={activeTypes}
                onFiltersChange={handleTypeChange}
                onOptionsUpdate={handleTypeOptionsUpdate}
                title="Filtrar por tipo"
                filterKey={`person-${personId}-trainings-types`}
                icon={<FaGraduationCap />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={topicOptions}
                activeFilters={activeTopics}
                onFiltersChange={handleTopicChange}
                onOptionsUpdate={handleTopicOptionsUpdate}
                title="Filtrar por tema"
                filterKey={`person-${personId}-trainings-topics`}
                icon={<FaBook />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={instructorOptions}
                activeFilters={activeInstructors}
                onFiltersChange={handleInstructorChange}
                onOptionsUpdate={handleInstructorOptionsUpdate}
                title="Filtrar por instructor"
                filterKey={`person-${personId}-trainings-instructors`}
                icon={<FaChalkboardTeacher />}
                variant="outline-secondary"
              />
            </div>
            
            {/* ✅ BOTÓN DESHABILITADO SI ESTÁ DADA DE BAJA */}
            <Button 
              size="sm"
              className="d-flex align-items-center"
              onClick={handleOpenAssignModal}
              disabled={isPersonDeactivated}
            >
              <FaPlus className="me-1" /> Asignar Capacitación
            </Button>
          </div>
        </Col>
      </Row>
      
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper 
          columnDefs={trainingColumns}
          rowData={filteredTrainings}
          tableType="capacitaciones"
          onView={handleViewTraining}
          onDelete={handleUnassignTraining}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 10,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      <AssignTrainingModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        personId={personId}
        currentTrainings={trainings}
        onTrainingAssigned={handleTrainingAssigned}
      />
      
      <Modal 
        show={showUnassignModal} 
        onHide={() => setShowUnassignModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Dar de baja de capacitación</Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <p>¿Está seguro que desea dar de baja a esta persona de la capacitación?</p>
          {selectedTraining && (
            <div className="mt-2 p-3 bg-light rounded">
              <strong>Tipo:</strong> {getTipoDescripcion(selectedTraining.type, TIPOS_CAPACITACIONES)}<br />
              <strong>Fecha:</strong> {dayjs(selectedTraining.date).format(FORMATO_FECHA)}<br />
              {selectedTraining.topic && <><strong>Tema:</strong> {selectedTraining.topic}<br /></>}
              {selectedTraining.instructor && <><strong>Instructor:</strong> {selectedTraining.instructor}</>}
            </div>
          )}
          <p className="text-muted small mt-3">Esta acción eliminará a la persona de la lista de participantes de esta capacitación.</p>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowUnassignModal(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmUnassign}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Procesando...
              </>
            ) : (
              'Confirmar baja'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TrainingsSection;